// Library Imports
import {
  useQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";

// Local Imports
import axiosInstance from "../../redux/network/https";
import { endPoints, queryKeys } from "../../redux/network/constant";

// ── Shared Types ──────────────────────────────────────────────────────────────

export interface ApiResponse<T> {
  data: T;
  message: string;
  success: boolean;
}

export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

// ── Partner User ──────────────────────────────────────────────────────────────

export interface PartnerUser {
  id: number;
  first_name: string;
  last_name: string;
  full_name?: string;
  profession: string | null;
  industry?: string | null;
  profile_image: string | null;
  mailing_address?: string | null;
  operated_address?: string | null;
  user_description?: string | null;
  email?: string;
  phone_no?: string;
  birthday?: string | null;
  is_verified?: boolean;
  user_roles?: string[];
  user_interests?: string[];
  skills?: string[];
  experiences?: unknown[];
  project_experiences?: unknown[];
  education?: unknown[];
  certifications?: unknown[];
  licenses?: unknown[];
  recommendations?: unknown[];
  reviews_received?: unknown[];
  portfolio_images?: string[];
}

// ── Relationship ──────────────────────────────────────────────────────────────

export interface PartnerRelationship {
  status: string | null;
  request_status: string | null;
  sender_relationship_label: string | null;
  receiver_relationship_label: string | null;
}

// ── Partner List Items ────────────────────────────────────────────────────────

export interface MatchedPartnerItem {
  id: number;
  matched_user: PartnerUser;
  sender_relationship_label: string | null;
  receiver_relationship_label: string | null;
  created_at: string;
  updated_at: string;
}

export interface PendingPartnerItem {
  id: number;
  partner: PartnerUser;
  sender_relationship_label: string | null;
  receiver_relationship_label: string | null;
  created_at: string;
  updated_at: string;
}

export interface RequestedPartnerItem {
  id: number;
  user: PartnerUser;
  sender_relationship_label: string | null;
  receiver_relationship_label: string | null;
  created_at: string;
  updated_at: string;
}

export interface PrioritizedPartnerItem {
  id: number;
  partner: PartnerUser;
  sender_relationship_label: string | null;
  receiver_relationship_label: string | null;
  created_at: string;
  updated_at: string;
}

export interface ClientNetworkItem extends PartnerUser {
  project_count?: number;
}
// ── Partners Listing Response ─────────────────────────────────────────────────

export interface PartnersListingResponse {
  suggested_partners?: PaginatedResponse<PartnerUser>;
  matched_partners?: PaginatedResponse<MatchedPartnerItem>;
  nearest_partners?: PaginatedResponse<PartnerUser>;
  prioritized_partners?: PaginatedResponse<PrioritizedPartnerItem>;
  pending_partners?: PaginatedResponse<PendingPartnerItem>;
  requested_partners?: PaginatedResponse<RequestedPartnerItem>;
  clients?: PaginatedResponse<ClientNetworkItem>;
}

// ── Suggested Partner Detail ──────────────────────────────────────────────────

export interface SuggestedPartnerDetail extends PartnerUser {
  relationship: PartnerRelationship | null;
  status?: string | null;
  request_status?: string | null;
  type?: string | null;
}

// ── Filter / Search ───────────────────────────────────────────────────────────

export interface NetworkFilterInput {
  search?: string;
  industry?: string;
  profession?: string;
  city?: string;
  postalcode?: string;
  skills?: string[] | string;
  interests?: string[] | string;
}

export interface FilteredPartnersFilters {
  industry?: string;
  profession?: string;
  city?: string;
  skills?: string[];
  interests?: string[];
  search?: string;
  postalcode?: string;
  zipCode?: string;
}

// ── Pagination ────────────────────────────────────────────────────────────────

export interface PartnersPagination {
  suggested_page?: number;
  suggested_page_size?: number;
  matched_page?: number;
  matched_page_size?: number;
  nearest_page?: number;
  nearest_page_size?: number;
  prioritized_page?: number;
  prioritized_page_size?: number;
  pending_page?: number;
  pending_page_size?: number;
  requested_page?: number;
  requested_page_size?: number;
  client_page?: number;
  client_page_size?: number;
}

// ── Mutation Variables ────────────────────────────────────────────────────────

export interface UpdatePartnerStatusVariables {
  id: number;
  status: string;
  relationship_label?: string;
}

// ── Fetch signup suggestions ──────────────────────────────────────────────────

const fetchSignupSuggestions = async (): Promise<{
  suggested_partners: PaginatedResponse<PartnerUser>;
}> => {
  const { data } = await axiosInstance.get<ApiResponse<{
    suggested_partners: PaginatedResponse<PartnerUser>;
  }>>(endPoints.SignupSuggestions);
  return data?.data;
};

export const useSignupSuggestions = (enabled = true) => {
  return useQuery({
    queryKey: queryKeys.SignupSuggestions(),
    queryFn: fetchSignupSuggestions,
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  });
};

// ── Fetch function for network filter search ──────────────────────────────────

const fetchNetworkFilter = async ({
  queryKey,
}: {
  queryKey: readonly unknown[];
}): Promise<PartnerUser[] | unknown[]> => {
  const [, input] = queryKey;

  // Normalize input to an object
  const filters =
    typeof input === "string" ? { search: input } : (input as NetworkFilterInput) || {};

  const { search, profession, city, postalcode, skills, interests } = filters;

  const params: Record<string, string> = {};

  // Ensure search is a string
  if (search) {
    const searchStr =
      typeof search === "string" ? search : String(search || "");
    params.search = searchStr.trim();
  }

  // Ensure profession is a string
  if (profession) {
    const profStr =
      typeof profession === "string" ? profession : String(profession || "");
    params.profession = profStr;
  }

  // Ensure city is a string
  if (city) {
    const cityStr = typeof city === "string" ? city : String(city || "");
    params.city = cityStr;
  }

  // Ensure postalcode is a string
  if (postalcode) {
    const posStr =
      typeof postalcode === "string" ? postalcode : String(postalcode || "");
    params.postalcode = posStr;
  }

  if (skills) {
    params.skills = Array.isArray(skills) ? skills.join(",") : skills;
  }
  if (interests) {
    params.interests = Array.isArray(interests)
      ? interests.join(",")
      : interests;
  }

  const { data } = await axiosInstance.get<ApiResponse<PartnerUser[] | PaginatedResponse<PartnerUser>>>(
    endPoints.NetworkFilter,
    { params }
  );

  // Search response returns paginated object directly
  // Non-search response returns object with partner arrays
  const responseData = data?.data;

  // If search is performed, return the paginated results array
  if (search && (responseData as PaginatedResponse<PartnerUser>)?.results) {
    return (responseData as PaginatedResponse<PartnerUser>).results;
  }

  return (responseData as PartnerUser[]) || [];
};

// React Query hook for searching partners in user's network
export const useNetworkFilterSearch = (search: string, enabled = true) => {
  const trimmed = typeof search === "string" ? search.trim() : "";
  const shouldEnable = enabled && trimmed.length >= 2;

  return useQuery({
    queryKey: queryKeys.NetworkFilter(trimmed),
    queryFn: fetchNetworkFilter,
    enabled: shouldEnable,
    staleTime: 30 * 1000,
  });
};

// ── Fetch user's matched partners (network count) ─────────────────────────────

const fetchUserNetwork = async (): Promise<{ count: number }> => {
  try {
    const { data } = await axiosInstance.get<ApiResponse<PartnersListingResponse>>(
      endPoints.partnerListing,
      {
        params: {
          matched_page: 1,
          matched_page_size: 100,
        },
      }
    );

    const matchedSection = normalizeSection(data?.data?.matched_partners);
    return { count: matchedSection.count };
  } catch (error) {
    console.error("Error fetching user network:", error);
    return { count: 0 };
  }
};

export const useUserNetworkCount = (enabled = true) => {
  return useQuery({
    queryKey: queryKeys.UserNetwork(),
    queryFn: fetchUserNetwork,
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// ── Fetch suggested partner details ───────────────────────────────────────────

const fetchSuggestedPartnerDetails = async ({
  queryKey,
}: {
  queryKey: readonly unknown[];
}): Promise<SuggestedPartnerDetail | undefined> => {
  const [, , id] = queryKey;
  const { data } = await axiosInstance.get<ApiResponse<SuggestedPartnerDetail>>(
    endPoints.SuggestedPartnersDetails + id
  );

  return data?.data;
};

export const useSuggestedPartnerDetails = (id: number | string | undefined) => {
  return useQuery({
    queryKey: queryKeys.SuggestedPartnerDetails(id),
    queryFn: fetchSuggestedPartnerDetails,
    enabled: !!id,
  });
};

// ── Helper: normalize a section that may be a raw array or a paginated object ─

export const normalizeSection = <T,>(
  section: PaginatedResponse<T> | T[] | undefined | null
): PaginatedResponse<T> => {
  if (!section)
    return { results: [], count: 0, next: null, previous: null };
  if (Array.isArray(section)) {
    return {
      results: section,
      count: section.length,
      next: null,
      previous: null,
    };
  }
  return section;
};

// ── Fetch partners listing with optional per-section pagination ───────────────

const fetchPartnersListing = async ({
  queryKey,
}: {
  queryKey: readonly unknown[];
}): Promise<PartnersListingResponse | undefined> => {
  const [, , pagination] = queryKey;

  const params: Record<string, string | number> = {};
  if (pagination) {
    Object.entries(pagination as PartnersPagination).forEach(([key, value]) => {
      if (value != null && value !== "") {
        params[key] = value;
      }
    });
  }

  const { data } = await axiosInstance.get<ApiResponse<PartnersListingResponse>>(
    endPoints.partnerListing,
    {
      params: Object.keys(params).length > 0 ? params : undefined,
    }
  );

  return data?.data;
};

export const usePartnersListing = (
  pagination?: PartnersPagination,
  enabled = true,
  options: Record<string, unknown> = {}
) => {
  return useQuery({
    queryKey: queryKeys.PartnersListing(pagination || null),
    queryFn: fetchPartnersListing,
    enabled,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    refetchInterval: false,
    refetchIntervalInBackground: false,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

/**
 * Returns partners listing data with optional pagination.
 */
export const useSuspensePartnersListing = (pagination?: PartnersPagination) => {
  return useQuery({
    queryKey: queryKeys.PartnersListing(pagination || null),
    queryFn: fetchPartnersListing,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    staleTime: 5 * 60 * 1000,
  });
};

// ── Filter partners (industry, profession, city, skills, interests, search, postalcode) ─

const fetchFilteredPartners = async ({
  queryKey,
}: {
  queryKey: readonly unknown[];
}): Promise<unknown> => {
  const [, , filters] = queryKey;

  const {
    industry,
    profession,
    city,
    skills = [],
    interests = [],
    search,
    postalcode,
  } = (filters as FilteredPartnersFilters) || {};

  const params = new URLSearchParams();

  if (industry?.trim()) {
    params.append("industry", industry);
  }

  if (profession?.trim()) {
    params.append("profession", profession);
  }
  if (postalcode?.trim()) {
    params.append("postalcode", postalcode);
  }

  if (search?.trim()) {
    params.append("search", search);
  }
  if (city?.trim()) {
    params.append("city", city);
  }

  const skillsClean = Array.isArray(skills) ? skills.filter(Boolean) : [];
  if (skillsClean.length > 0) params.append("skills", skillsClean.join(","));

  const interestsClean = Array.isArray(interests)
    ? interests.filter(Boolean)
    : [];

  if (interestsClean.length > 0) {
    params.append("interests", interestsClean.join(","));
  }

  let url = endPoints.partnerFilter;
  const qs = params.toString();
  if (qs) url += `?${qs}`;

  const { data } = await axiosInstance.get<ApiResponse<unknown>>(url);
  const responseData = data?.data;

  // Search response returns paginated object with results array directly
  if (search?.trim() && (responseData as PaginatedResponse<unknown>)?.results) {
    return (responseData as PaginatedResponse<unknown>).results;
  }

  return responseData;
};

export const useFilteredPartners = (
  filters?: FilteredPartnersFilters,
  enabled = true
) => {
  const key = {
    industry: filters?.industry || "",
    profession: filters?.profession || "",
    city: filters?.city || "",
    skills: Array.isArray(filters?.skills) ? [...filters.skills] : [],
    interests: Array.isArray(filters?.interests) ? [...filters.interests] : [],
    search: filters?.search || "",
    postalcode: filters?.postalcode || filters?.zipCode || "",
  };

  const shouldEnable =
    enabled &&
    Object.values(key).some((v) => {
      if (Array.isArray(v)) return v.length > 0;
      return typeof v === "string" ? v.trim().length > 0 : !!v;
    });

  return useQuery({
    queryKey: queryKeys.FilteredPartners(key),
    queryFn: fetchFilteredPartners,
    enabled: shouldEnable,
  });
};

// ── Fetch roles and interests ─────────────────────────────────────────────────

const fetchRolesAndInterests = async (): Promise<{
  roles: string[];
  interests: string[];
}> => {
  const { data } = await axiosInstance.get<ApiResponse<{
    roles: string[];
    interests: string[];
  }>>(endPoints.RolesInterests);
  return data?.data;
};

export const useRolesAndInterests = (enabled = true) => {
  return useQuery({
    queryKey: queryKeys.RolesInterests(),
    queryFn: fetchRolesAndInterests,
    enabled: !!enabled,
    staleTime: 10 * 60 * 1000, // 10 minutes - roles/interests don't change often
    refetchOnWindowFocus: false,
  });
};

// ── Update partner status mutation ────────────────────────────────────────────

const postPartnerStatus = async ({
  id,
  status,
  relationship_label = "",
}: UpdatePartnerStatusVariables): Promise<unknown> => {
  let endpoint = endPoints.partnerStatus + id + "/interest/";
  let payload: Record<string, unknown> = { interest: status, relationship_label };

  if (status === "ACCEPT" || status === "REJECT") {
    endpoint = endPoints.partnerStatus + id + "/request-response/";
    payload = { action: status, relationship_label };
  }

  const { data } = await axiosInstance.post<ApiResponse<unknown>>(endpoint, payload);
  return data?.data;
};

export const useUpdatePartnerStatus = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: postPartnerStatus,
    onMutate: async (variables) => {
      // Cancel outgoing refetches so they don't overwrite our optimistic update
      await queryClient.cancelQueries({
        queryKey: queryKeys.SignupSuggestions(),
      });

      // Snapshot the previous value
      const previousSuggestions = queryClient.getQueryData(
        queryKeys.SignupSuggestions()
      );

      // Optimistically remove the partner from suggested_partners
      queryClient.setQueryData(queryKeys.SignupSuggestions(), (old: unknown) => {
        const oldData = old as {
          suggested_partners?: PaginatedResponse<PartnerUser>;
        } | null;
        const suggestedPartners = oldData?.suggested_partners;
        if (!suggestedPartners?.results) return old;
        return {
          ...oldData,
          suggested_partners: {
            ...suggestedPartners,
            results: suggestedPartners.results.filter(
              (partner) => partner.id !== variables.id
            ),
          },
        };
      });

      // Return a context object with the snapshotted value
      return { previousSuggestions };
    },
    onError: (_err, _variables, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousSuggestions) {
        queryClient.setQueryData(
          queryKeys.SignupSuggestions(),
          context.previousSuggestions
        );
      }
    },
    onSuccess: (_data, variables) => {
      // Refresh related views (invalidate all pagination variants)
      queryClient.invalidateQueries({
        queryKey: ["partners", "listing"],
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.SuggestedPartnerDetails(variables?.id),
      });
    },
    onSettled: () => {
      // Always refetch after error or success
      queryClient.invalidateQueries({
        queryKey: queryKeys.SignupSuggestions(),
      });
    },
  });
};
